/**
 * Centralized TTL (Time To Live) configuration for all storage systems
 * @author AI Assistant
 * @date 2025-01-17
 */

export class StorageTTLConfig {
  
  // ========================================
  // BASE TIME UNITS (in milliseconds)
  // ========================================
  
  /** 1 minute in milliseconds */
  public static readonly MINUTE_MS = 60 * 1000;
  
  /** 1 hour in milliseconds */
  public static readonly HOUR_MS = 60 * StorageTTLConfig.MINUTE_MS;
  
  /** 1 day in milliseconds */
  public static readonly DAY_MS = 24 * StorageTTLConfig.HOUR_MS;
  
  /** 1 week in milliseconds */
  public static readonly WEEK_MS = 7 * StorageTTLConfig.DAY_MS;
  
  /** 1 month (30 days) in milliseconds */
  public static readonly MONTH_MS = 30 * StorageTTLConfig.DAY_MS;
  
  // ========================================
  // STORAGE RECORDS (General key-value storage)
  // ========================================
  
  /** Default TTL for general storage records (30 days) */
  public static readonly STORAGE_RECORDS_TTL = StorageTTLConfig.MONTH_MS;
  
  /** TTL for survey constructs (30 days - important offline data) */
  public static readonly SURVEY_CONSTRUCT_TTL = StorageTTLConfig.MONTH_MS;
  
  /** TTL for form fields (30 days - needed for offline functionality) */
  public static readonly FORM_FIELDS_TTL = StorageTTLConfig.MONTH_MS;
  
  // ========================================
  // PENDING ANSWERS (Offline survey responses)
  // ========================================
  
  /** TTL for pending answers (30 days - important data that needs sync) */
  public static readonly PENDING_ANSWERS_TTL = StorageTTLConfig.MONTH_MS;
  
  // ========================================
  // TEMPORARY ANSWERS (In-progress survey data)
  // ========================================
  
  /** TTL for temporary answers (7 days - session data) */
  public static readonly TEMPORARY_ANSWERS_TTL = StorageTTLConfig.WEEK_MS;
  
  // ========================================
  // ERROR HANDLING
  // ========================================
  
  /** TTL for storage error records (24 hours) */
  public static readonly STORAGE_ERRORS_TTL = StorageTTLConfig.DAY_MS;
  
  // ========================================
  // SPECIALIZED TTL VALUES
  // ========================================
  
  /** Very short TTL for testing (1 minute) */
  public static readonly TEST_SHORT_TTL = StorageTTLConfig.MINUTE_MS;
  
  /** Short TTL for temporary cache (1 hour) */
  public static readonly CACHE_SHORT_TTL = StorageTTLConfig.HOUR_MS;
  
  /** Medium TTL for session data (24 hours) */
  public static readonly SESSION_TTL = StorageTTLConfig.DAY_MS;
  
  /** Long TTL for persistent data (90 days) */
  public static readonly PERSISTENT_TTL = 90 * StorageTTLConfig.DAY_MS;
  /** TTL for duration/audit backups (90 days) */
  public static readonly DURATION_AUDIT_TTL = 90 * StorageTTLConfig.DAY_MS;
  
  // ========================================
  // UTILITY METHODS
  // ========================================
  
  /**
   * Get TTL by type
   * @param type - The type of data
   * @returns TTL in milliseconds
   */
  public static getTTLByType(type: 'storage' | 'pending' | 'temporary' | 'form-fields' | 'survey-construct' | 'errors' | 'cache' | 'session' | 'persistent'): number {
    switch (type) {
      case 'storage':
        return StorageTTLConfig.STORAGE_RECORDS_TTL;
      case 'pending':
        return StorageTTLConfig.PENDING_ANSWERS_TTL;
      case 'temporary':
        return StorageTTLConfig.TEMPORARY_ANSWERS_TTL;
      case 'form-fields':
        return StorageTTLConfig.FORM_FIELDS_TTL;
      case 'survey-construct':
        return StorageTTLConfig.SURVEY_CONSTRUCT_TTL;
      case 'errors':
        return StorageTTLConfig.STORAGE_ERRORS_TTL;
      case 'cache':
        return StorageTTLConfig.CACHE_SHORT_TTL;
      case 'session':
        return StorageTTLConfig.SESSION_TTL;
      case 'persistent':
        return StorageTTLConfig.PERSISTENT_TTL;
      default:
        return StorageTTLConfig.STORAGE_RECORDS_TTL;
    }
  }
  
  /**
   * Convert TTL to human readable format
   * @param ttlMs - TTL in milliseconds
   * @returns Human readable string
   */
  public static formatTTL(ttlMs: number): string {
    const days = Math.floor(ttlMs / StorageTTLConfig.DAY_MS);
    const hours = Math.floor((ttlMs % StorageTTLConfig.DAY_MS) / StorageTTLConfig.HOUR_MS);
    const minutes = Math.floor((ttlMs % StorageTTLConfig.HOUR_MS) / StorageTTLConfig.MINUTE_MS);
    
    if (days > 0) {
      return hours > 0 ? `${days} días y ${hours} horas` : `${days} días`;
    } else if (hours > 0) {
      return minutes > 0 ? `${hours} horas y ${minutes} minutos` : `${hours} horas`;
    } else {
      return `${minutes} minutos`;
    }
  }
  
  /**
   * Get all TTL configurations for debugging/info
   */
  public static getAllTTLConfigs(): {[key: string]: {ms: number, formatted: string}} {
    return {
      'Storage Records': {
        ms: StorageTTLConfig.STORAGE_RECORDS_TTL,
        formatted: StorageTTLConfig.formatTTL(StorageTTLConfig.STORAGE_RECORDS_TTL)
      },
      'Pending Answers': {
        ms: StorageTTLConfig.PENDING_ANSWERS_TTL,
        formatted: StorageTTLConfig.formatTTL(StorageTTLConfig.PENDING_ANSWERS_TTL)
      },
      'Temporary Answers': {
        ms: StorageTTLConfig.TEMPORARY_ANSWERS_TTL,
        formatted: StorageTTLConfig.formatTTL(StorageTTLConfig.TEMPORARY_ANSWERS_TTL)
      },
      'Survey Constructs': {
        ms: StorageTTLConfig.SURVEY_CONSTRUCT_TTL,
        formatted: StorageTTLConfig.formatTTL(StorageTTLConfig.SURVEY_CONSTRUCT_TTL)
      },
      'Form Fields': {
        ms: StorageTTLConfig.FORM_FIELDS_TTL,
        formatted: StorageTTLConfig.formatTTL(StorageTTLConfig.FORM_FIELDS_TTL)
      },
      'Storage Errors': {
        ms: StorageTTLConfig.STORAGE_ERRORS_TTL,
        formatted: StorageTTLConfig.formatTTL(StorageTTLConfig.STORAGE_ERRORS_TTL)
      }
    };
  }
  
  /**
   * Create expiration timestamp from current time + TTL
   * @param ttlMs - TTL in milliseconds
   * @returns Timestamp when data should expire
   */
  public static createExpirationTimestamp(ttlMs: number): number {
    return Date.now() + ttlMs;
  }
  
  /**
   * Check if a timestamp is expired
   * @param expiresAt - Expiration timestamp
   * @returns True if expired
   */
  public static isExpired(expiresAt: number): boolean {
    return Date.now() > expiresAt;
  }
}
